import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { FormBuilder } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { catchError, finalize, of } from 'rxjs';
import { MultiplePropertyListingDto, PropertyListingDto, TaskListDto, UserDto } from '../../../../../../app/services/property-matrixV2/models';
import { LookupService, PropertyListingService, UserManagementService } from '../../../../../../app/services/property-matrixV2/services';
import { ListingStateService } from '../../../../../../app/shared/services/listing-state-service/listing-state.service';
import { NotificationService } from '../../../../../../app/shared/services/notification-service/notification.service';

@Component({
  selector: 'app-allocate-planners',
  templateUrl: './allocate-planners.component.html',
  styleUrls: ['./allocate-planners.component.scss', '../../../../../../css/2-modules/_admin-portal.scss']
})
export class AllocatePlannersComponent implements OnInit {

  @Input() selectedListing: PropertyListingDto;
  @Input() selectedListingGroup: MultiplePropertyListingDto;
  @Output() hideAddendumsEvent = new EventEmitter<boolean>();
  @Output() levelOneReport = new EventEmitter<boolean>();
  @Output() levelTwoReport = new EventEmitter<boolean>();

  sections = [
    { key: 1, value: 'Allocation', checked: true },
    { key: 2, value: 'Network Agent', checked: true },
    { key: 3, value: 'Planning Report', checked: true },
  ];
  reportType = [
    { key: 1, type: 'Level One Report', checked: false },
    { key: 2, type: 'Level Two Report', checked: false },
    { key: 3, type: 'Manual Report', checked: false },
  ];
  paymentType = [
    { key: 1, type: 'Upfront', checked: false },
    { key: 2, type: 'On Risk', checked: false }
  ];

  listingType: string;
  seniorPlanners: UserDto[] = [];
  juniorPlanners: UserDto[] = [];
  selectedSeniorPlanner: string | null | undefined = null;
  selectedJuniorPlanner: string | null | undefined = null;
  currentUser: UserDto | undefined;
  networkAgentType: number = 0;
  networkAgentTypeStatement: string = "";
  selectedReportType: string | null | undefined;
  selectedPaymentType: string | null | undefined;
  adminPortalAdministratorUserList: UserDto[] = [];
  firstAdminPortalAdministratorUser: UserDto | undefined = undefined;
  loading: boolean = false;
  paymentReceived: boolean = false;
  propertyListed: boolean = false;
  propertyDocumentRequirements: boolean = false;
  askingPrice: boolean = false;
  nonResidential: boolean = false;

  constructor(
    private route: ActivatedRoute,
    private userManagementService: UserManagementService,
    private lookupService: LookupService,
    private listingStateService: ListingStateService,
    private propertyListingService: PropertyListingService,
    private router: Router,
    private notificationService: NotificationService,
    private fb: FormBuilder,
  ) { }

  ngOnInit(): void {
    this.route.queryParams.subscribe(params => {
      this.listingType = params['listingType'];
    });
    this.getCurrentUser();
    this.getSeniorPlanners();
    this.getJuniorPlanners();
    this.getAdminPortalAdministratorUser();
    this.prePopulateData();
  }

  getSeniorPlanners(): void {
    this.userManagementService.apiV1UserManagementGetSeniorPlannerGet().subscribe(result => {
      if (result != null) {
        this.seniorPlanners = result;
        this.initializePlannerSelections();
      } else {
        this.notificationService.showWarningMessage('No Senior Planners', 'No senior planners found.');
      }
    });
  }

  getJuniorPlanners(): void {
    this.lookupService.apiV1LookupGetJuniorPlannerUsersGet().subscribe(result => {
      if (result != null) {
        this.juniorPlanners = result;
        this.initializePlannerSelections();
      } else {
        this.notificationService.showWarningMessage('No Junior Planners', 'No junior planners found.');
      }
    });
  }

  initializePlannerSelections(): void {
    if (!this.selectedListing || !this.selectedListing.taskList || !this.seniorPlanners.length || !this.juniorPlanners.length) {
      return;
    }
    this.selectedJuniorPlanner = this.selectedListing.juniorPlanner?.id;
    this.selectedSeniorPlanner = this.selectedListing.seniorPlanner?.id;
  }

  onSeniorPlannerChange(event: any): void {
    this.selectedSeniorPlanner = event.value;
  }

  onJuniorPlannerChange(event: any): void {
    this.selectedJuniorPlanner = event.value;
  }

  onReportTypeChange(event: any): void {
    this.selectedReportType = event.value;
  }

  onPaymentTypeChange(event: any): void {
    this.selectedPaymentType = event.value;
  }

  savePlanners(): void {
    let changedTaskLists = [] as TaskListDto[];
    if (this.selectedJuniorPlanner == null || this.selectedSeniorPlanner == null) {
      this.notificationService.showWarningMessage('No Planner Selected', 'Please select both a junior and senior planner.');
      return;
    }
    if (this.networkAgentType == 1) {
      this.networkAgentTypeStatement = "The property is listed directly bt the Agent";
    } else if (this.networkAgentType == 2) {
      this.networkAgentTypeStatement = "This property was referred to the Agent by PM";
    } else {
      this.notificationService.showWarningMessage('No network agent Selected', 'Please select network agent to continue.');
      return;
    }

    const selectedJuniorPlannerUser = this.juniorPlanners.find(planner => planner.id === this.selectedJuniorPlanner);
    const selectedSeniorPlannerUser = this.seniorPlanners.find(planner => planner.id === this.selectedSeniorPlanner);

    if (!selectedJuniorPlannerUser || !selectedSeniorPlannerUser) {
      this.notificationService.showErrorMessage('Error', 'Selected planners not found in the list.');
      return;
    }

    this.selectedListing.juniorPlanner = this.juniorPlanners.find(planner => planner.id === this.selectedJuniorPlanner);;
    this.selectedListing.seniorPlanner = this.seniorPlanners.find(planner => planner.id === this.selectedSeniorPlanner);
    this.selectedListing.networkAgentStatement = this.networkAgentTypeStatement;
    this.selectedListing.isPropertyListedDirectlyByAgent = this.networkAgentType == 1 ? true : false;
    this.selectedListing.allocatePlannerReportSelection = this.selectedReportType;
    this.selectedListing.paymentType = this.selectedPaymentType;
    this.selectedListing.paymentReceived = this.paymentReceived;
    this.selectedListing.propertyListed = this.propertyListed;
    this.selectedListing.propertyDocumentRequirements = this.propertyDocumentRequirements;
    this.selectedListing.askingPrice = this.askingPrice;
    this.selectedListing.nonResidential = this.nonResidential;

    this.propertyListingService.apiV1PropertyListingAddOrUpdatePlannersToPropertyListingPost({ body: this.selectedListing }).subscribe(result => {
      this.SavePlannerToService(result);
      if (result == true) {
        this.selectedListing.taskList?.forEach(task => {

          if (task.taskNumber?.number === 1) {
            if (task.taskStatus?.taskStatusNumber != 3) {
              task.user = this.currentUser;
              changedTaskLists.push(task);
            }
          }
          if (task.taskNumber?.number === 2) {
            task.user = selectedJuniorPlannerUser ?? undefined;
            changedTaskLists.push(task);
          }
          if (task.taskNumber?.number === 4) {
            task.user = selectedJuniorPlannerUser ?? undefined;
            changedTaskLists.push(task);
          }
          if (task.taskNumber?.number === 5) {
            task.user = selectedSeniorPlannerUser ?? undefined;
            changedTaskLists.push(task);
          }
          if (task.taskNumber?.number === 8) {
            task.user = selectedSeniorPlannerUser ?? undefined;
            changedTaskLists.push(task);
          }
          if (task.taskNumber?.number === 9) {
            if (task.taskStatus?.taskStatusNumber == 1) {
              task.user = this.firstAdminPortalAdministratorUser ?? this.currentUser;
              changedTaskLists.push(task);
            }
          }
          if (task.taskNumber?.number === 10) {
            if (task.taskStatus?.taskStatusNumber == 1) {
              task.user = selectedJuniorPlannerUser ?? undefined;
              changedTaskLists.push(task);
            }
          }
        });

        if (changedTaskLists.length > 0) {
          this.propertyListingService.apiV1PropertyListingUpdatePropertyListingTaskPost({ body: changedTaskLists, currentTaskNumber: 1 }).subscribe(result => {
            if (result != null) {
              this.notificationService.showSuccessMessage('Planners Updated', 'Planners have been updated successfully.');
              this.navigateToPlanningReport();
            } else {
              this.notificationService.showErrorMessage('Error', 'Failed to update planners.');
            }
          });
        }
        else {
          this.navigateToPlanningReport();
        }
      }
    });

  }

  private SavePlannerToService(successful: boolean): void {
    if (successful == true) {
      this.notificationService.showSuccessMessage("Success", "Successfully saved planners to listing.");
      this.listingStateService.setSelectedListing(this.selectedListing);
    }
    else {
      this.notificationService.showErrorMessage("Error", "Could not save planners to property listing.");
    }
  }

  getCurrentUser(): void {
    this.userManagementService.apiV1UserManagementGetCurrentUserGet().subscribe(result => {
      if (result != null) {
        this.currentUser = result;
      } else {
        this.notificationService.showWarningMessage('No Current User', 'No current user found.');
      }
    });
  }

  navigateToPlanningReport() {
    this.propertyListingService.apiV1PropertyListingGetPropertyListingByIdGet({ listingId: this.selectedListing.id }).subscribe(result => {
      this.selectedListing = result;
      this.listingStateService.setSelectedListing(this.selectedListing);
      this.router.navigate(['admin/listings/listing-overview'], {
        queryParams: {
          listingType: this.listingType
        }
      });
    });
  }

  prePopulateData(): void {
    if (this.selectedListing) {
      if (this.selectedListing.juniorPlanner != null) {
        this.selectedJuniorPlanner = this.selectedListing.juniorPlanner.id;
      }
      if (this.selectedListing.seniorPlanner != null) {
        this.selectedSeniorPlanner = this.selectedListing.seniorPlanner.id;
      }
      if (this.selectedListing.allocatePlannerReportSelection) {
        this.selectedReportType = this.selectedListing.allocatePlannerReportSelection;
      }
      if (this.selectedListing.paymentType) {
        this.selectedPaymentType = this.selectedListing.paymentType;
      }
      if (this.selectedListing.networkAgentStatement) {
        this.networkAgentTypeStatement = this.selectedListing.networkAgentStatement;

        if (this.selectedListing.isPropertyListedDirectlyByAgent == true) {
          this.networkAgentType = 1
        } else {
          this.networkAgentType = 2;
        }
      }
      if (this.selectedListing.paymentReceived) {
        this.paymentReceived = this.selectedListing.paymentReceived ?? false;
      }
      if (this.selectedListing.propertyListed) {
        this.propertyListed = this.selectedListing.propertyListed ?? false;
      }
      if (this.selectedListing.propertyDocumentRequirements) {
        this.propertyDocumentRequirements = this.selectedListing.propertyDocumentRequirements ?? false;
      }
      if (this.selectedListing.askingPrice) {
        this.askingPrice = this.selectedListing.askingPrice ?? false;
      }
      if (this.selectedListing.nonResidential) {
        this.nonResidential = this.selectedListing.nonResidential ?? false;
      }
    }
  }

  collapseOrExpandSection(section: { checked: boolean; }) {
    section.checked = !section.checked;
  }

  getAdminPortalAdministratorUser(): void {
    this.loading = true;
    this.lookupService.apiV1LookupGetAdminPortalAdministratorUsersGet({})
      .pipe(
        catchError((err) =>
          of(this.notificationService.showErrorMessage("Error", "Could not get the admin portal administration user."))
        ),
        finalize(() => {
          this.loading = false;
        })
      )
      .subscribe((result: UserDto[] | void) => {
        if (result && result.length > 0) {
          this.adminPortalAdministratorUserList = result;
          this.firstAdminPortalAdministratorUser = this.adminPortalAdministratorUserList[0] as UserDto;
        } else {
          this.notificationService.showWarningMessage("Warning", "There are no users found with a Admin portal administrator role.Please add user with that role.");
        }
      });
  }
}
